import Markdown from "react-markdown";

import styled from "styled-components";

import {
  getBorderRadius,
  getFontFamily,
  getFontSize,
  getFontWeight,
  getSemanticColor,
  getSpacing,
} from "@braze/beacon-styling";

import { HighchartsWrapper } from "@components/HighchartsWrapper";

export const StyledBrazeMarkdown = styled(Markdown)`
  & {
    color: ${getSemanticColor("neutral-text-default")};
    font-family: ${getFontFamily("base")};
    line-height: 1.4;
    overflow-wrap: anywhere;

    * {
      margin: 0;
      padding: 0;
      line-height: inherit;
    }

    // Rendered prose takes its colour from this container, so a caller that recolours the container
    // recolours the text. Listed element by element rather than with a universal selector: the global
    // stylesheet colours these directly - it has a rule for "p" and another for "p strong" - and plain
    // inheritance loses to both, while a universal selector would also outrank the class Beacon colours
    // links with, flattening every link into body text. Inline emphasis is listed for the same reason;
    // code and links are deliberately left out, as they carry a colour of their own.
    p,
    ul,
    ol,
    li,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    blockquote,
    strong,
    b,
    em,
    i,
    del {
      color: inherit;
    }

    & :first-child {
      margin-top: 0;
    }

    & > div {
      margin-top: ${getSpacing("md")};
    }

    hr {
      border-top: 1px solid ${getSemanticColor("neutral-border-decorative")};
      border-bottom: none;
      margin-top: ${getSpacing("md")};
    }

    p,
    ol,
    ul {
      line-height: 1.5;
      margin-top: ${getSpacing("md")};
    }

    li {
      margin-top: ${getSpacing("sm")};
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: ${getFontWeight("bold")};
      line-height: 1.4;
      margin-top: ${getSpacing("lg")};

      /* Reduce the margin-top of lists immediately after a heading. */
      & + ul,
      & + ol {
        margin-top: ${getSpacing("xs")};
      }
    }

    h1 {
      font-size: ${getFontSize("heading", 3)};
    }

    h2 {
      font-size: ${getFontSize("heading", 4)};
    }

    h3 {
      font-size: ${getFontSize("heading", 5)};
    }

    h4,
    h5,
    h6 {
      font-size: ${getFontSize("heading", 6)};
    }

    pre {
      background: none;
      border: none;
      margin-top: ${getSpacing("md")};
      white-space: pre-wrap;

      & > code,
      & > code[class*="language-"] {
        white-space: pre-wrap;
      }
    }

    ul,
    ol {
      margin-left: ${getSpacing("lg")};

      ul,
      ol {
        margin-top: 0;
      }
    }

    blockquote {
      border-left: 0;
      margin-top: ${getSpacing("md")};
      padding-left: ${getSpacing("md")};
      position: relative;

      &::before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background-color: ${getSemanticColor("neutral-border-decorative")};
        border-radius: ${getBorderRadius("round")};
      }

      p {
        font-size: inherit;
        font-weight: inherit;
      }
    }
  }
`;

export const StyledHighchartsWrapper = styled(HighchartsWrapper)`
  max-width: 330px;
  width: 100%;
`;
